import type { TradingPair } from '../../types/pair';
import type { OHLCV, Timeframe } from '../../types/ohlcv';
import type { Ticker24h } from '../../types/ticker';
import { ProviderError } from '../types';
import { aggregateToTimeframe, bucketStart } from '../../utils/aggregate';
import { timeframeToMs } from '../../utils/timeframe';
import { ReconnectingSocket } from './ws';
import type { AdapterOptions, ExchangeAdapter } from './types';
// Shared with the sibling adapters until these move to a common module.
import { bucketEnd, decimalsOfIncrement, mapWithConcurrency } from './coinbase';
import { CandleRollup } from './kraken';

/**
 * Bitvavo adapter (public endpoints, no key, CORS `*`; 1000 weight per minute
 * per IP, a candle call weighs 1).
 *
 * Verified live 2026-10-02:
 *   - GET /v2/{BASE-QUOTE}/candles?interval=I&start=ms&end=ms&limit=N returns
 *     `[ts ms, open, high, low, close, volume]` rows (strings) NEWEST first; when
 *     more match than `limit` (max 1440) the newest are returned. A candle is
 *     included when ts ≥ start and it closes by `end`; the forming one is
 *     included once `end` reaches its bucket end. Intervals 1m 5m 15m 30m 1h 2h
 *     4h 6h 8h 12h 1d 1W 1M (3m and 3d are rejected); all UTC-aligned, 1W on
 *     Monday, 1M on the 1st. Intervals without trades have no row. History
 *     reaches back to the market's listing. Responses are CDN-cached per URL for
 *     ~30 s, so windows reaching the present end at the current millisecond.
 *   - GET /v2/markets: status 'trading'. `pricePrecision` (significant digits)
 *     is null on every market now; `tickSize` ("1.00", "0.0000010") is the price
 *     step and `quantityDecimals` the amount decimals.
 *   - GET /v2/ticker/24h: open/high/low/last/volume/volumeQuote strings; `open`
 *     can be in exponent form ("7.418E+4"); markets without trades in 24h have
 *     null open/high/low/volume.
 *   - wss://ws.bitvavo.com/v2/: `candles` sends `{event:'candle', market,
 *     interval, candle:[[...]]}` only after trades (≤ 1/s, no snapshot), for
 *     1m–1d only — 1W and 1M are REST-only, so streams roll them up from days;
 *     `ticker24h` sends `{event:'ticker24h', data:[...]}`; `{"action":"getTime"}`
 *     is answered, which keeps a quiet socket alive.
 */

const REST_URL = 'https://api.bitvavo.com/v2';
const WS_URL = 'wss://ws.bitvavo.com/v2/';
const MAX_ROWS = 1440;
const MAX_CONCURRENT_REQUESTS = 6;
/** Most finer candles fetched to roll up one request. */
const MAX_SOURCE_CANDLES = 5000;
const GET_TIME = { intervalMs: 25_000, message: '{"action":"getTime"}' };

/** Native intervals. */
const INTERVALS: Partial<Record<Timeframe, string>> = {
  '1m': '1m',
  '5m': '5m',
  '15m': '15m',
  '30m': '30m',
  '1h': '1h',
  '2h': '2h',
  '4h': '4h',
  '6h': '6h',
  '12h': '12h',
  '1D': '1d',
  '1W': '1W',
  '1M': '1M',
};

/** Timeframes Bitvavo has no interval for, and the native one they are rolled up from. */
const ROLLUP_SOURCE: Partial<Record<Timeframe, Timeframe>> = {
  '3m': '1m',
  '3D': '1D',
};

/** The candles channel rejects 1W and 1M ("interval parameter is invalid."), so streams build them from days. */
const STREAM_ROLLUP_SOURCE: Partial<Record<Timeframe, Timeframe>> = { ...ROLLUP_SOURCE, '1W': '1D', '1M': '1D' };

/** The native timeframe (and its interval name) fetched over REST to serve `timeframe`. */
export function bitvavoInterval(timeframe: Timeframe): { source: Timeframe; interval: string } {
  return intervalFrom(ROLLUP_SOURCE, timeframe);
}

/** The native timeframe (and its interval name) streamed to serve `timeframe`. */
export function bitvavoStreamInterval(timeframe: Timeframe): { source: Timeframe; interval: string } {
  return intervalFrom(STREAM_ROLLUP_SOURCE, timeframe);
}

function intervalFrom(
  rollupSource: Partial<Record<Timeframe, Timeframe>>,
  timeframe: Timeframe,
): { source: Timeframe; interval: string } {
  const source = rollupSource[timeframe] ?? timeframe;
  const interval = INTERVALS[source];
  if (!interval) throw new ProviderError(`Unsupported timeframe ${timeframe}`);
  return { source, interval };
}

export function toBitvavoMarket(pair: string): string {
  return pair.trim().toUpperCase().replace('/', '-');
}

/**
 * Split `[from, to]` into bucket-aligned windows of at most 1440 candles,
 * oldest first. `end` is the open of the window's last candle.
 */
export function bitvavoWindows(from: number, to: number, timeframe: Timeframe): Array<{ start: number; end: number }> {
  // 1M steps are approximate (30 days), but 1440 of them span a century, so months never need a second window.
  const step = timeframeToMs(timeframe);
  const windows: Array<{ start: number; end: number }> = [];
  let start = Math.max(0, bucketStart(from, timeframe));
  while (start <= to) {
    const end = Math.min(to, start + (MAX_ROWS - 1) * step);
    windows.push({ start, end });
    start = end + step;
  }
  return windows;
}

/** Candle rows → oldest-first OHLCV (Bitvavo sends newest first). */
export function mapBitvavoCandles(rows: unknown): OHLCV[] {
  if (!Array.isArray(rows)) throw new ProviderError('Unrecognized Bitvavo candle response');
  const out: OHLCV[] = [];
  for (const row of rows as unknown[]) {
    if (!Array.isArray(row) || row.length < 6) continue;
    const [timestamp, open, high, low, close, volume] = row.slice(0, 6).map(toNumber);
    if (timestamp === undefined || open === undefined || high === undefined || low === undefined) continue;
    if (close === undefined || volume === undefined) continue;
    out.push({ timestamp, open, high, low, close, volume });
  }
  out.sort((a, b) => a.timestamp - b.timestamp);
  return out;
}

export interface BitvavoMarket {
  market: string;
  status: string;
  base: string;
  quote: string;
  tickSize?: string;
  quantityDecimals?: number;
}

/** `/markets` → trading pairs sorted by symbol. */
export function mapBitvavoMarkets(markets: BitvavoMarket[]): TradingPair[] {
  return markets
    .filter((m) => m.status === 'trading')
    .map((m) => ({
      symbol: `${m.base}/${m.quote}`,
      base: m.base,
      quote: m.quote,
      exchangeId: 'bitvavo',
      // `pricePrecision` counts significant digits (and is null now); the tick size gives real decimals.
      priceDecimals: decimalsOfIncrement(m.tickSize),
      quantityDecimals: m.quantityDecimals ?? 8,
    }))
    .sort((a, b) => a.symbol.localeCompare(b.symbol));
}

export interface BitvavoTicker {
  market?: string;
  timestamp?: number;
  open?: string | null;
  high?: string | null;
  low?: string | null;
  last?: string | null;
  bid?: string | null;
  ask?: string | null;
  volume?: string | null;
  volumeQuote?: string | null;
}

/** A `/ticker/24h` entry or `ticker24h` event item → ticker; null without a last price. */
export function mapBitvavoTicker(ticker: BitvavoTicker): Ticker24h | null {
  const last = toNumber(ticker?.last);
  if (typeof ticker?.market !== 'string' || last === undefined || last <= 0) return null;
  const open = toNumber(ticker.open);
  // Null volume means no trades in the last 24h.
  const volume = toNumber(ticker.volume) ?? 0;
  return {
    exchangeId: 'bitvavo',
    pair: ticker.market.replace('-', '/'),
    last,
    open24h: open,
    high24h: toNumber(ticker.high),
    low24h: toNumber(ticker.low),
    changePercent24h: open ? ((last - open) / open) * 100 : 0,
    baseVolume24h: volume,
    quoteVolume24h: toNumber(ticker.volumeQuote) ?? volume * last,
    bid: toNumber(ticker.bid),
    ask: toNumber(ticker.ask),
    timestamp: typeof ticker.timestamp === 'number' ? ticker.timestamp : Date.now(),
  };
}

interface WsFrame {
  event?: string;
  market?: string;
  interval?: string;
  candle?: unknown;
  data?: unknown;
}

/** A `candle` event for `market` at `interval` → its candles, oldest first; [] for any other frame. */
export function parseBitvavoCandleFrame(raw: string, market: string, interval: string): OHLCV[] {
  const frame = parseFrame(raw);
  if (!frame || frame.event !== 'candle' || frame.market !== market || frame.interval !== interval) return [];
  return Array.isArray(frame.candle) ? mapBitvavoCandles(frame.candle) : [];
}

/** A `ticker24h` event → tickers; [] for any other frame. */
export function parseBitvavoTickerFrame(raw: string): Ticker24h[] {
  const frame = parseFrame(raw);
  if (!frame || frame.event !== 'ticker24h' || !Array.isArray(frame.data)) return [];
  const tickers: Ticker24h[] = [];
  for (const item of frame.data as BitvavoTicker[]) {
    const ticker = mapBitvavoTicker(item);
    if (ticker) tickers.push(ticker);
  }
  return tickers;
}

export function bitvavoAdapter(options: AdapterOptions = {}): ExchangeAdapter {
  async function getJson<T>(path: string): Promise<T> {
    const doFetch = options.fetch ?? globalThis.fetch;
    const response = await doFetch(`${REST_URL}${path}`, { headers: { Accept: 'application/json' } });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const message =
        body && typeof body === 'object' && typeof (body as { error?: unknown }).error === 'string'
          ? (body as { error: string }).error
          : `Bitvavo request failed (${response.status})`;
      throw new ProviderError(message, response.status, body);
    }
    return (await response.json()) as T;
  }

  /** Native candles in [from, to], paged under the 1440-row cap, oldest first. */
  async function fetchCandles(market: string, timeframe: Timeframe, from: number, to: number): Promise<OHLCV[]> {
    const { interval } = bitvavoInterval(timeframe);
    const pages = await mapWithConcurrency(bitvavoWindows(from, to, timeframe), MAX_CONCURRENT_REQUESTS, ({ start, end }) => {
      const query = new URLSearchParams({
        interval,
        start: String(start),
        // Bitvavo leaves out candles that close after `end` (the forming one excepted). A window
        // reaching the present ends now, to the ms: a repeated URL would be served from the CDN cache.
        end: String(Math.min(bucketEnd(end, timeframe), Date.now())),
        limit: String(MAX_ROWS),
      });
      return getJson<unknown>(`/${market}/candles?${query}`).then(mapBitvavoCandles);
    });
    const merged = new Map<number, OHLCV>();
    for (const page of pages) for (const candle of page) merged.set(candle.timestamp, candle);
    return [...merged.values()].sort((a, b) => a.timestamp - b.timestamp);
  }

  return {
    id: 'bitvavo',
    name: 'Bitvavo',

    async getPairs(): Promise<TradingPair[]> {
      return mapBitvavoMarkets(await getJson<BitvavoMarket[]>('/markets'));
    },

    async getOHLCV({ pair, timeframe, from, to }): Promise<OHLCV[]> {
      const market = toBitvavoMarket(pair);
      const { source } = bitvavoInterval(timeframe);
      const inWindow = (candle: OHLCV) => candle.timestamp >= from && candle.timestamp <= to;
      if (source === timeframe) return (await fetchCandles(market, timeframe, from, to)).filter(inWindow);
      // Start on a bucket boundary so the first rolled-up candle is complete, and cap the finer fetch.
      const start = Math.max(
        bucketStart(from, timeframe),
        bucketStart(to - MAX_SOURCE_CANDLES * timeframeToMs(source), timeframe),
      );
      // Like a native candle, the bucket holding `to` comes back whole, so fetch to its end.
      const fine = await fetchCandles(market, source, start, bucketEnd(to, timeframe) - 1);
      return aggregateToTimeframe(fine, timeframe).filter(inWindow);
    },

    async getTickers(): Promise<Ticker24h[]> {
      const tickers: Ticker24h[] = [];
      for (const item of await getJson<BitvavoTicker[]>('/ticker/24h')) {
        const ticker = mapBitvavoTicker(item);
        if (ticker) tickers.push(ticker);
      }
      return tickers;
    },

    subscribeOHLCV(pair, timeframe, onCandle, onStatus) {
      const market = toBitvavoMarket(pair);
      const { source, interval } = bitvavoStreamInterval(timeframe);
      const rolledUp = source !== timeframe;
      const rollup = new CandleRollup(timeframe);
      // A rolled-up candle is only right once REST has filled the bucket; native ones are whole.
      let ready = !rolledUp;
      let stopped = false;
      let lastEmitted = -Infinity;
      let connections = 0;

      const emit = (candle: OHLCV | null) => {
        if (!candle || !ready || stopped || candle.timestamp < lastEmitted) return;
        lastEmitted = candle.timestamp;
        onCandle(candle);
      };
      // The stream has no snapshot and is silent until the next trade, so REST supplies the bucket in progress.
      const seed = () => {
        const now = Date.now();
        fetchCandles(market, source, bucketStart(now, timeframe), now).then(
          (candles) => {
            if (stopped) return;
            ready = true;
            emit(rollup.seed(candles));
          },
          () => {
            if (stopped) return;
            // Carry on with what streams in; a rolled-up bucket may lack its earlier part.
            ready = true;
            onStatus?.('error');
            emit(rollup.current());
          },
        );
      };

      seed();
      const socket = new ReconnectingSocket({
        urls: [WS_URL],
        WebSocket: options.WebSocket,
        onStatus,
        heartbeat: GET_TIME,
        onOpen: (send) => {
          if (connections++ > 0) {
            // What streamed before the drop may be stale; the seed rebuilds the bucket.
            rollup.reset();
            ready = !rolledUp;
            seed();
          }
          send(JSON.stringify({ action: 'subscribe', channels: [{ name: 'candles', interval: [interval], markets: [market] }] }));
        },
        onMessage: (raw) => {
          for (const candle of parseBitvavoCandleFrame(raw, market, interval)) emit(rollup.update(candle));
        },
      });
      return () => {
        stopped = true;
        socket.close();
      };
    },

    subscribeTickers(pairs, onTicker) {
      const markets = pairs.map(toBitvavoMarket);
      if (markets.length === 0) return () => {};
      const socket = new ReconnectingSocket({
        urls: [WS_URL],
        WebSocket: options.WebSocket,
        heartbeat: GET_TIME,
        onOpen: (send) => {
          // One unknown market fails a whole subscribe action (verified live), so each market gets its own.
          for (const market of markets) {
            send(JSON.stringify({ action: 'subscribe', channels: [{ name: 'ticker24h', markets: [market] }] }));
          }
        },
        onMessage: (raw) => {
          for (const ticker of parseBitvavoTickerFrame(raw)) onTicker(ticker);
        },
      });
      return () => socket.close();
    },
  };
}

function parseFrame(raw: string): WsFrame | null {
  try {
    const frame = JSON.parse(raw) as unknown;
    return frame && typeof frame === 'object' ? (frame as WsFrame) : null;
  } catch {
    return null;
  }
}

/** Finite number from a venue's string or number field (exponent form included); undefined otherwise. */
function toNumber(value: unknown): number | undefined {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number.parseFloat(value) : Number.NaN;
  return Number.isFinite(n) ? n : undefined;
}
